class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.selectedVariants = [];
    this.observeResize();
    this.swiper = new Swiper(this, {
      allowTouchMove: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      on: {
        beforeSlideChangeStart: () => {
          this.onSlideChange();
        }
      },
    });
    this.onBeginBuilder('.splash button');
    this.onClickVariant('.variants__grid__cell');
    this.onClickNextStep('.step-controls__next');
    this.onClickPreviousStep('.step-controls__prev');
    this.subtotalLabels = this.querySelectorAll('span[data-total]');
    this.successResetButton = this.querySelector('.success-step .step-controls .button');

    if (this.successResetButton) {
      this.successResetButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.resetBundle();
      });
    }
  }

  /**
   * Scroll variant containers to the top before each slide change.
   */
  onSlideChange() {
    const variantContainers = this.querySelectorAll('.swiper-slide .variants');
    variantContainers.forEach((container) => {
      container.scrollTo(0, 0);
    });
  }

  /**
   * Adds a variant to the bundle.
   * @param {HTMLElement} variant
   */
   addToBundle(variant) {
    const variantIndex = this.findVariantInBundle(variant);

    if (variantIndex > -1) {
      return;
    }

    variant.setAttribute('aria-selected', 'true');
    this.selectedVariants.push(variant);
  }

  /**
   * Removes a variant from the bundle.
   * @param {HTMLElement} variant
   */
  removeFromBundle(variant) {
    const variantIndex = this.findVariantInBundle(variant);

    if (variantIndex < 0) {
      return;
    }

    variant.setAttribute('aria-selected', 'false');
    this.selectedVariants.splice(variantIndex, 1);
  }

  /**
   * Returns the index of the first found variant for a specific step.
   * @param {HTMLElement} variant
   * @returns {number}
   */
  findVariantInBundle(variant) {
    return this.selectedVariants.findIndex((element) => {
      return element.dataset.id === variant.dataset.id &&
        element.dataset.step === variant.dataset.step;
    });
  }

  /**
   * Bundle builder takes up the remaining viewport on mobile by determining
   * the height of the header and promo bar first.
   */
  observeResize() {
    let header = document.getElementById('shopify-section-header');

    if (!header) {
      header = document.getElementById('shopify-section-header--mobile-first');
    }

    if (!header) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      this.style.setProperty('--header-height', `${height}px`);
    });

    observer.observe(header);
  }

  /**
   * Begins the bundle builder when clicking the splash screen's button.
   * @param {string} selector
   */
  onBeginBuilder(selector) {
    const button = this.querySelector(selector);

    if (!button) {
      // Splash screen is optional.
      return;
    }

    button.addEventListener('click', (e) => {
      e.preventDefault();
      this.swiper.slideNext();
    });
  }

  /**
   * Updates the state of a variant displayed within a step's grid.
   * @param {string} selector
   */
  onClickVariant(selector) {
    const callback = (e) => {
      if (e.target.dataset.modalBundleItem) {
        return;
      }

      const variant = e.target.closest(selector);

      if (!variant || variant.classList.contains('sold-out')) {
        return;
      }

      const multiSelectableStep = variant.closest('[aria-multiselectable="true"]');
      // Get attributes to support Firefox.
      const ariaDisabled = variant.getAttribute('aria-disabled');
      const ariaSelected = variant.getAttribute('aria-selected');

      if (ariaDisabled === 'true' && multiSelectableStep) {
        // Selection limit reached.
        return;
      }

      if (ariaDisabled === 'true' && !multiSelectableStep) {
        // Replace the previously selected variant for this one.
        const previousVariant = variant.parentElement.querySelector('[aria-selected="true"]');
        if (!previousVariant) {
          return;
        }
        this.removeFromBundle(previousVariant);
        variant.setAttribute('aria-disabled', 'false');
      }

      e.preventDefault();

      if (ariaSelected === 'false') {
        this.addToBundle(variant);
      } else {
        this.removeFromBundle(variant);
      }

      this.updateCurrentStep(variant.closest('.swiper-slide'));
      this.updateBundleSubtotal();
    };

    this.addEventListener('click', callback);
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') {
        return;
      }

      if (e.repeat) {
        return;
      }

      callback(e);
    });
  }

  /**
   * Updates the current builder step. Some products may get disabled, enabled,
   * and the total bundle pricing may get updated.
   * @param {HTMLElement} slide
   */
  updateCurrentStep(slide) {
    if (!slide) {
      return;
    }

    const requiredChoices = parseInt(slide.dataset.requiredChoices, 10);
    const selectedVariants = slide.querySelectorAll('[aria-selected="true"]');
    const nextStepButton = slide.querySelector('.step-controls__next');

    if (selectedVariants.length === requiredChoices) {
      // With a completed selection all other options are now disabled.
      const unselectedVariants = slide.querySelectorAll('[aria-selected="false"]:not(.sold-out)');
      unselectedVariants.forEach((variant) => {
        variant.setAttribute('aria-disabled', 'true');
      });
      slide.dataset.stepComplete = true;
      nextStepButton.disabled = false;
      return;
    }

    const disabledVariants = slide.querySelectorAll('[aria-disabled="true"]:not(.sold-out)');
    disabledVariants.forEach((variant) => {
      variant.setAttribute('aria-disabled', 'false');
    });
    slide.dataset.stepComplete = false;
    nextStepButton.disabled = true;
  }

  /**
   * Progresses the bundle builder to the next step/slide.
   * @param {string} selector
   */
  onClickNextStep(selector) {
    this.addEventListener('click', (e) => {
      const button = e.target.closest(selector);

      if (!button) {
        return;
      }

      e.preventDefault();

      if (button.dataset.createBundle === 'true') {
        this.createBundle();
        return;
      }

      this.swiper.slideNext();
    });
  }

  /**
   * Returns to the previous step/slide.
   * @param {string} selector
   */
  onClickPreviousStep(selector) {
    this.addEventListener('click', (e) => {
      if (!e.target.closest(selector)) {
        return;
      }
      e.preventDefault();
      this.swiper.slidePrev();
    });
  }

  /**
   * Calculates the bundle's subtotal then updates the user interface.
   */
  updateBundleSubtotal() {
    const subtotal = this.selectedVariants.reduce((subtotal, variant) => {
      const price = parseFloat(variant.dataset.price, 10);
      return subtotal + price;
    }, 0.0);

    this.dataset.subtotal = subtotal;

    this.subtotalLabels.forEach((label) => {
      label.innerText = theme.utils.toMoney(subtotal);
    });
  }

  getItemJSON(id, bundleGroupId, step) {
    return {
      id,
      quantity: 1,
      properties: {
        _bundle_id: this.dataset.handle,
        _bundle_group_id: bundleGroupId,
        _bundle_title: this.dataset.title,
        _bundle_image: this.dataset.image,
        _bundle_qty: 1,
        _bundle_step: step,
      },
    }
  }

  /**
   * Creates the bundle, adds it to the cart, then redirects
   * the user to the cart page.
   */
  createBundle() {
    const bundleGroupId = Math.floor(Date.now() / 1000);
    const parentVariant = parseInt(this.dataset.variant);
    let variants = this.selectedVariants
      .filter((item) => !item.classList.contains('optional'))
      .map((item) => {
        const id = parseInt(item.dataset.id, 10);
        const noShip = item.dataset.noShip;
        const step = parseInt(item.dataset.step, 10);
        const itemToAdd = this.getItemJSON(id, bundleGroupId, step);

        if (noShip) {
          itemToAdd.properties._noship = noShip;
        }

        return itemToAdd;
      });

    if (parentVariant) variants.push(this.getItemJSON(parentVariant, bundleGroupId, 0));

    theme.cart.add(variants).then(() => {
      if (this.successResetButton) {
        this.swiper.slideNext();
      }
    });
  }

  /**
   * Reset the bundle builder to its initial state.
   */
  resetBundle() {
    this.selectedVariants = [];
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', 'false');
    });
    this.querySelectorAll('[aria-disabled="true"]').forEach((element) => {
      element.setAttribute('aria-disabled', 'false');
    });
    this.updateBundleSubtotal();
    this.swiper.slideTo(1);
  }
}

customElements.define('bundle-builder', BundleBuilder);
